/* eslint-disable array-callback-return */
// REACT IMPORTS
import { useEffect, useMemo, useState } from "react";
import AddressComp from "components/payments/AddressComp";

// COMPONENT IMPORTS
import {
  paymentLogoGet,
  paymentReceiptAgreementDownloadPdf,
  paymentReceiptDownloadPdf,
  paymentReceiptGet,
} from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Skeleton from "react-loading-skeleton";

// ICONS IMPORTS
import logo from "icons/smallLogo.svg";
import { CloseIcon } from "icons/payments/CloseIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { useUserData } from "store/slices/authSlice";
import QuestionsFooter from "components/auth/QuestionsFooter";
import { InfoIcon } from "icons/payments/InfoIcon";
import { ExportIcon } from "icons/payments/ExportIcon";
import PaymentPreview from "./PaymentPreview";
import {
  changePaymentScheduleName,
  changeScheduleName,
  downloadPdfFromApi,
  encode,
  formatCurrency,
  getPaymentMethod,
} from "utils/HelperFunctions";
import Button from "components/form/Button";
import CustomTooltip from "components/ui-component/CustomTooltip";
import {
  useLeadData,
  usePortalSettingsData,
  usePortalVanityUrl,
} from "store/slices/dashboardSlice";
import { useParams } from "react-router-dom";
import { formatPaymentListDate } from "utils/DateFormat";
import InvoicePreview from "components/actionCenter/InvoicePreview";
import { decode } from "utils/HelperFunctions";
import moment from "moment";
import { SettingsValue } from "types/payments";
import Drawer from "components/ui-component/SideDrawer";
import { useMediaQuery } from "react-responsive";
import FailedPayment from "components/payments/FailedPayment";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { RootState } from "store/store";
import {
  isCacelledBefore,
  isCancelsSoon,
  isEnded,
  isEndSoon,
} from "utils/Formater/DateFormat";
import { OfferIcon } from "icons/payments/OfferIcon";

// PROPS TYPE
interface SubscriptionsPreviewProps {
  id: number;
  setId: any;
  show: boolean;
  setShow: any;
  setHandleDrawerSize?: any;
  isScheduled?: boolean | false;
  isApiCall?: boolean | true;
  isActionCenter?: boolean | false;
  clusterId?: string;
  onNestedPreviewChange?: (isOpen: boolean) => void;
}

/* ------------------------------------- PAYMENT PREVIEW ------------------------------------- */

const SubscriptionsPreview = (props: SubscriptionsPreviewProps) => {
  const {
    id,
    setId,
    show,
    setShow,
    setHandleDrawerSize,
    isScheduled,
    isApiCall = true,
    isActionCenter = false,
    clusterId: propClusterId,
    onNestedPreviewChange,
  } = props;

  const isLessThan680 = useMediaQuery({ query: "(max-width:680px)" });
  const dispatch = useAppDispatch();
  const portalSettings = usePortalSettingsData();
  const vanityUrl = usePortalVanityUrl();
  const { paymentLogo } = useAppSelector((state: RootState) => state.payment);
  const accountLeadData = useLeadData();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [orderData, setOrderData] = useState<any>({});
  const [cppData, setCppData] = useState<any>({});
  const [scheduledDetails, setScheduledDetails] = useState<any>({});
  const [scheduled, setScheduled] = useState<any>([]);
  const [isPaymentPreview, setIsPaymentPreview] = useState<boolean>(false);
  const [isPaymentPreviewId, setIsPaymentPreviewId] = useState<any>();
  const [isInvoicePreview, setIsInvoicePreview] = useState<boolean>(false);
  const [isInvoicePreviewId, setIsInvoicePreviewId] = useState<
    string | undefined
  >(undefined);
  const [isDownloading, setIsDownloading] = useState("");
  const [invoiceList, setInvoiceList] = useState<any[]>([]);
  const userData = useUserData();
  const { clusterId: cluster_id } = useParams();
  const clusterId = propClusterId || decode(cluster_id || "", true);
  const { financialSettings } = useAppSelector(
    (state: RootState) => state.auth
  );
  const [agreementData, setAgreementData] = useState<any>({});
  const [failedPayment, setFailedPayment] = useState<boolean>(false);
  const [failedPaymentId, setFailedPaymentId] = useState<string | null>(null);
  const [isAgreementDownload, setIsAgreementDownload] =
    useState<boolean>(false);

  const onGetPaymentReceipt = async () => {
    setIsLoading(true);

    try {
      let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
      let res: any = await dispatch(
        paymentReceiptGet({
          id: id,
          pid,
          source: isScheduled ? "dc_orders" : "clients_schedule_custom",
        })
      ).unwrap();
      if (res && res?.body?.data) {
        if (res?.body?.data?.paymentData) {
          let data = { ...res?.body?.data?.paymentData };
          setOrderData({
            ...data,
            agreementid: res?.body?.data?.agreementData?.id,
            pay_amount: data?.amount,
            c_FeeAmount:
              data?.payment_method === "CC"
                ? data?.cc_fee_amount
                  ? data?.cc_fee_amount
                  : null
                : data?.ach_fee_amount
                  ? data?.ach_fee_amount
                  : null,
            grandTotal:
              data?.amount +
              (data?.payment_method === "CC"
                ? data?.cc_fee_amount
                  ? data?.cc_fee_amount
                  : 0
                : data?.ach_fee_amount
                  ? data?.ach_fee_amount
                  : 0),
          });
          setCppData({ ...res?.body?.data?.cppData });
        } else {
          let orderData = res?.body?.data?.orderData;
          setAgreementData(res?.body?.data?.agreementData);
          let productAmount =
            orderData?.productSubscriptions &&
            orderData?.productSubscriptions?.length > 0
              ? orderData?.productSubscriptions
                  .filter(
                    (x: any) => x?.priceDetails?.frequency === "recurring"
                  )
                  .reduce((acc: number, curr: any) => {
                    acc += curr?.amount * curr?.quantity;
                    return acc;
                  }, 0)
              : 0;

          let data = {
            ...orderData,
            pay_amount: res?.body?.data?.amount
              ? parseFloat(res?.body?.data?.amount)
              : 0,
            grandTotal: res?.body?.data?.grandTotal
              ? parseFloat(res?.body?.data?.grandTotal)
              : 0,
            c_FeeAmount: res?.body?.data?.totalconveFeeAmount
              ? parseFloat(res?.body?.data?.totalconveFeeAmount)
              : null,
            productAmount: productAmount,
            agreementid: orderData?.agreementid || orderData?.agreement_id,
          };
          let scheduledData = { ...res?.body?.data?.getCustomerData };
          let scheduledArr = [...res?.body?.data?.clientsScheduleCustomData];
          setOrderData({
            ...data,
          });
          setCppData({ ...res?.body?.data?.cppData });
          setScheduledDetails({
            ...scheduledData,
          });
          setInvoiceList([...res?.body?.data?.invoiceList]);
          setScheduled([...scheduledArr]);
        }
      }
    } catch (error: any) {
      console.log("Get payment receipt err: -->", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onPaymentDownload = async (id: any, receiptId?: any) => {
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    let payload = {
      invoice_id: id,
      lead_id: accountLeadData?.lead_id,
      url: `${vanityUrl}view-payment/${pid}`,
      cluster_id: orderData?.clusterId ?? clusterId,
      isFromPortal: true,
      // emails: [],
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Receipt - ${receiptId || id}.pdf`,
      (loading) => setIsDownloading(loading ? id : "")
    );
  };

  const onGetLogo = async () => {
    try {
      let payload = {
        cluster_id: clusterId,
      };
      await dispatch(paymentLogoGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get logo err: -->", error?.message);
    }
  };

  useEffect(() => {
    if (!paymentLogo && clusterId && show) onGetLogo();
  }, [clusterId, paymentLogo]);

  useEffect(() => {
    if (!id && !show) return;
    if (isApiCall) {
      onGetPaymentReceipt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, show]);

  useEffect(() => {
    onNestedPreviewChange?.(isPaymentPreview || isInvoicePreview);
  }, [isPaymentPreview, isInvoicePreview, onNestedPreviewChange]);

  const onClose = () => {
    if (isPaymentPreview || isInvoicePreview) {
      setIsPaymentPreview(false);
      setIsPaymentPreviewId("");
      setIsInvoicePreview(false);
      setIsInvoicePreviewId("");
    } else {
      onNestedPreviewChange?.(false);
      setId("");
      setShow(false);
      setScheduled([]);
      setScheduledDetails({});
      setOrderData({});
      setHandleDrawerSize(false);
      setInvoiceList([]);
      setIsLoading(true);
    }
  };

  const getStatusName = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return { label: "Paid", className: "bg-[#23B3390D] text-green" };
      case "draft":
        return { label: "Draft", className: "bg-[#6D77A00D] text-iconBright" };
      case "opened":
        return { label: "Open", className: "bg-[#f3f8fe] text-blue" };
      case "open":
        return { label: "Open", className: "bg-[#f3f8fe] text-blue" };
      case "pending":
        return { label: "Pending", className: "bg-[#FDB6000D] text-yellow" };
      case "overdue":
        return { label: "Overdue", className: "bg-[#FDB6000D] text-yellow" };
      case "pastdue":
        return { label: "Past due", className: "bg-[#FDB6000D] text-yellow" };
      case "disputed":
        return {
          label: "Disputed",
          className: "bg-[#6D77A00D] text-iconBright",
        };
      case "partial_payment":
        return {
          label: "Partial payment",
          className: "bg-[#23B3390D] text-green",
        };
      case "cancelled":
        return { label: "Canceled", className: "bg-[#fef2f2] text-[#FF0000]" };
      case "refunded":
        return { label: "Refunded", className: "bg-[#f3f8fe] text-blue" };
      case "uc":
        return {
          label: "Uncollectible",
          className: "bg-[#fef2f2] text-[#FF0000]",
        };
      default:
        return { label: "--", className: "" };
    }
  };

  const handleDownloadAgreement = async () => {
    let agreement_slug = orderData?.agreementid;
    let plan_id = scheduledDetails?.plan_id || orderData?.plan_id;
    const url = window.location.href;
    const baseUrl = new URL(url).origin;
    let payload = {
      plan_id: plan_id,
      cluster_id: orderData?.clusterId,
      url: `${baseUrl}/agreement/${agreement_slug}`,
    };

    await downloadPdfFromApi(
      () => dispatch(paymentReceiptAgreementDownloadPdf(payload)).unwrap(),
      `Payment_Agreement - ${plan_id}.pdf`,
      setIsAgreementDownload
    );
  };

  const isCancelledAtPeriodEnd = useMemo(() => {
    if (!orderData) return false;

    return (
      orderData?.recurring_type === "subscription" &&
      scheduledDetails?.canceled_at !== null &&
      isCancelsSoon(scheduledDetails?.canceled_at)
    );
  }, [orderData, scheduledDetails]);

  const products = useMemo(() => {
    if (!orderData?.productSubscriptions) return [];
    return orderData?.productSubscriptions;
  }, [orderData]);

  const payment_date = useMemo(() => {
    if (!scheduledDetails || !orderData) return "";
    if (orderData?.recurring_type === "subscription") {
      const isEndsSoon = isEndSoon({
        date: orderData?.end_sub,
        frequency: scheduledDetails?.payment_schedule,
        intervalCount: products?.[0]?.priceDetails?.interval_count || 1,
        intervalSelector:
          products?.[0]?.priceDetails?.interval_selector || "day",
      });

      const isAlreadyEnd = isEnded(orderData?.end_sub);

      const isCancelledBeforePeriodEnd = isCacelledBefore(
        scheduledDetails?.canceled_at,
        orderData?.end_sub
      );

      if (isEndsSoon || isAlreadyEnd) {
        return (
          (isCancelledBeforePeriodEnd
            ? scheduledDetails?.canceled_at
            : orderData?.end_sub) || scheduledDetails?.next_billing_date
        );
      } else if (
        orderData?.sub_status === "CANCELLED" ||
        isCancelledAtPeriodEnd
      ) {
        return (
          scheduledDetails?.canceled_at || scheduledDetails?.next_billing_date
        );
      } else {
        return scheduledDetails?.next_billing_date;
      }
    } else {
      return "";
    }
  }, [scheduledDetails, orderData, products, isCancelledAtPeriodEnd]);

  const dateStatusAndDesc = useMemo(() => {
    if (!scheduledDetails || !orderData) return { status: "", desc: "" };
    if (orderData?.recurring_type === "subscription") {
      const isEndsSoon = isEndSoon({
        date: orderData?.end_sub,
        frequency: scheduledDetails?.payment_schedule,
        intervalCount: products?.[0]?.priceDetails?.interval_count || 1,
        intervalSelector:
          products?.[0]?.priceDetails?.interval_selector || "day",
      });

      const isAlreadyEnd = isEnded(orderData?.end_sub);

      const isCancelledBeforePeriodEnd = isCacelledBefore(
        scheduledDetails?.canceled_at,
        orderData?.end_sub
      );

      const isCancelSoon = isCancelsSoon(scheduledDetails?.canceled_at);

      if ((isEndsSoon || isAlreadyEnd) && !isCancelledBeforePeriodEnd) {
        return {
          status: isAlreadyEnd ? "Ended on" : "Ends on",
          desc: isAlreadyEnd
            ? "The date when your subscription ended. No further charges will be made."
            : "The date when your subscription will end, and you will no longer be billed.",
        };
      } else if (
        orderData?.sub_status === "CANCELLED" ||
        isCancelledAtPeriodEnd
      ) {
        return {
          status: isCancelSoon ? "Cancels on" : "Canceled on",
          desc: isCancelSoon
            ? "The date when your subscription will be canceled."
            : "The date when your subscription was canceled.",
        };
      } else {
        return {
          status: "Renews on",
          desc: "The date when your subscription will automatically renew, and you will be charged for the next billing period.",
        };
      }
    } else {
      return { status: "", desc: "" };
    }
  }, [orderData, scheduledDetails, payment_date, isCancelledAtPeriodEnd]);

  const isOffer = useMemo(() => {
    if (!agreementData || !scheduledDetails) return false;

    if (agreementData?.settlement_offer === SettingsValue.YES) {
      return true;
    }

    if (
      (scheduledDetails?.offer_type !== "" ||
        scheduledDetails?.offer_type !== null) &&
      scheduledDetails?.offerDetails
    ) {
      return true;
    }

    return false;
  }, [agreementData, scheduledDetails]);

  return (
    <div
      className={`${
        isInvoicePreview || isPaymentPreview
          ? "bg-bgPreviewSecondary"
          : "bg-bgSecondary"
      } h-[var(--shell-vh)] overflow-y-auto pt-[70px]`}
    >
      <div
        className={`${
          isInvoicePreview || isPaymentPreview
            ? "bg-bgPreviewHeaderSecondary"
            : "bg-bgPrimary"
        } lg:px-10 px-5 py-5 bg-bgPrimary flex items-center justify-between fixed top-0 w-full z-[3]`}
      >
        <div className="flex items-center gap-2">
          <EyeIcon
            strokeColor="var(--color-txt-primary)"
            width="24"
            height="24"
          />
          <div className="lg:text-[26px] text-[22px] text-txtPrimary font-medium leading-[30px]">
            Preview
          </div>
        </div>
        <div className="flex items-center gap-5">
          {orderData?.agreementid && orderData?.agreementid !== "" && (
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => {
                handleDownloadAgreement();
              }}
            >
              {isAgreementDownload ? (
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              ) : (
                <DownloadIcon
                  strokeColor="var(--color-btn-primary-active)"
                  width="16"
                  height="16"
                />
              )}
              <span className="text-sm font-medium text-btnPrimaryActive hover:!text-btnPrimaryHover leading-normal underline">
                {isAgreementDownload ? "Downloading..." : "Download"}
              </span>
            </div>
          )}
          <div
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon />
          </div>
        </div>
      </div>

      {!isLoading &&
        (orderData?.plan_id > 0 || scheduledDetails?.plan_id > 0) && (
          <div
            className={`lg:px-10 px-5 py-4  lg:text-base text-sm ${scheduledDetails?.last_payment_status?.toLowerCase() === "cancelled" ? "bg-lightRed2" : "bg-lightGreen"}`}
          >
            Payment agreement{" "}
            {scheduledDetails?.last_payment_status?.toLowerCase() ===
            "cancelled"
              ? "canceled"
              : "completed"}{" "}
            on{" "}
            <span
              className={
                scheduledDetails?.last_payment_status?.toLowerCase() ===
                "cancelled"
                  ? "text-red"
                  : "text-green"
              }
            >
              {moment(
                (scheduledDetails?.last_payment_status?.toLowerCase() ===
                "cancelled"
                  ? agreementData?.cancel_date
                  : agreementData?.completion_date) ||
                  scheduledDetails?.creation_date ||
                  orderData?.insert_date
              ).format("MMMM D, YYYY")}
            </span>
          </div>
        )}

      {/* logo */}
      <div className="py-[16px] lg:px-10 px-5 flex items-center justify-between">
        <div className="lg:text-xl text-lg font-normal leading-normal text-txtPrimary">
          {isLoading ? (
            <Skeleton height={20} width={120} />
          ) : orderData?.plan_id > 0 || scheduledDetails?.plan_id > 0 ? (
            "Payment Agreement"
          ) : orderData?.description === "Payment Agreement" ||
            orderData?.payment_note === "Payment Agreement" ? (
            "Payment Agreement"
          ) : orderData?.recurring_type !== "schedule" ? (
            "Subscription"
          ) : (
            "Custom Plan"
          )}
        </div>
        <div className="flex items-center justify-end gap-[10px] ">
          {isLoading ? (
            <Skeleton width={150} height={36} />
          ) : paymentLogo ? (
            <div className="h-16 lg:h-16 flex justify-end">
              <img
                src={paymentLogo}
                alt=""
                className="animate__animated animate__fadeIn object-contain"
              />
            </div>
          ) : (
            <img
              src={logo}
              alt=""
              className="animate__animated animate__fadeIn"
            />
          )}
          {!paymentLogo && (
            <div className="text-purple font-medium text-xl leading-[30px]">
              CompanyName
            </div>
          )}
        </div>
      </div>

      {/* Account details */}
      <div className="py-[8px] lg:px-10 px-5 flex items-center justify-between flex-col gap-[8px]">
        <div className="w-full text-start text-xl text-blue leading-[26.05px]">
          {isLoading ? (
            <Skeleton width={120} height={21} />
          ) : (
            <>
              {orderData?.first_name ? orderData.first_name : "--"}{" "}
              {orderData?.last_name && orderData.last_name}
            </>
          )}
        </div>
        <div className="w-full py-[8px] flex justify-between">
          <div className="text-sm leading-[18.23px]">
            <span className="text-txtSecondary">Account ID</span>{" "}
            <span className="text-txtPrimary">
              {isLoading ? (
                <Skeleton width={60} height={12} />
              ) : (
                orderData?.accountId
              )}
            </span>
          </div>
          <div className="text-sm leading-[18.23px]">
            <span className="text-txtSecondary">Balance</span>{" "}
            <span className="text-txtPrimary">
              {isLoading ? (
                <Skeleton width={60} height={12} />
              ) : (
                formatCurrency(orderData?.balance) || "$0.00"
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Billing details */}
      <div className="lg:px-10 px-5 pb-2">
        <div
          className={`lg:px-6 px-4 py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg`}
        >
          <div className="flex flex-col items-start gap-5">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary ">
              Billed to
            </div>
            <div className="h-[1px] w-full bg-strokePrimary" />
            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary">
                  Account ID
                </div>
                <div className="font-normal text-sm leading-normal text-txtPrimary">
                  {isLoading ? (
                    <Skeleton width={80} height={12} />
                  ) : (
                    orderData?.accountId
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary ">
                  Customer
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={150} height={12} />
                  ) : (
                    `${orderData?.first_name || ""} ${orderData?.last_name || ""}`
                  )}
                </div>
              </div>
            </div>
            <AddressComp
              orderData={orderData}
              cppData={cppData}
              isLoading={isLoading}
              addressData={orderData}
            />
            {/* <div className="h-[1px] w-full bg-strokePrimary" /> */}
          </div>
        </div>
        <div className="lg:px-6 px-4 pb-5 bg-bgPrimary border-x border-b border-strokePrimary rounded-b-lg">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
              Payment method
            </div>
            <div className="flex items-center gap-2 text-txtPrimary">
              {isLoading ? (
                <Skeleton width={150} />
              ) : (
                getPaymentMethod({
                  card_type: orderData?.card_type,
                  method: orderData?.payment_method,
                  digits: orderData?.card_last_4_digits,
                  account_number: orderData?.card_last_4_digits,
                  className: "!gap-[2px]",
                  textClassName: "!text-txtPrimary",
                  isHidden: true,
                  bank_name: orderData?.bank_name,
                  rowId: id,
                  payment_collection_type:
                    orderData?.recurring_type === "schedule"
                      ? "MOF"
                      : orderData?.payment_collection_type,
                }) || "--"
              )}
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="lg:px-10 px-5 py-2">
          <div className="lg:px-6 px-4  py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
            <div className="flex flex-col items-start gap-5">
              <Skeleton width={100} />
              <div className="h-[1px] w-full bg-strokeSecondary" />
              <div className="flex flex-col items-start gap-4 w-full">
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                    Start date
                    <CustomTooltip
                      maxwidth="430px"
                      place="top"
                      id="start-date-anchor"
                      content={`<div class="text-xs leading-[18px] font-normal max-w-[250px]">The date when the first payment installment is scheduled to be processed.</div>`}
                    >
                      <InfoIcon
                        id="start-date-anchor"
                        className="cursor-pointer group"
                        _className="group-hover:!stroke-iconObscure"
                      />
                    </CustomTooltip>
                  </div>
                  <Skeleton width={80} />
                </div>
                <div className="h-[1px] w-full bg-strokeSecondary" />
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                    Frequency{" "}
                    <CustomTooltip
                      maxwidth="430px"
                      place="top"
                      id="frequency-anchor"
                      content={`<div class="text-xs leading-[18px] font-normal">Defines how often the installments will be processed.</div>`}
                    >
                      <div
                        id="frequency-anchor"
                        className="cursor-pointer pointer-events-auto"
                      >
                        <InfoIcon />
                      </div>
                    </CustomTooltip>
                  </div>
                  <Skeleton width={100} />
                </div>
                <div className="h-[1px] w-full bg-strokeSecondary" />

                <div className="flex items-center justify-between w-full">
                  <div className="">
                    <Skeleton width={150} />
                  </div>
                  <div className="font-medium text-sm leading-normal text-txtPrimary text-right capitalize">
                    <Skeleton width={50} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:px-6 px-4  py-5 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg">
            <div className="flex items-center justify-between w-full">
              <div className="text-xl leading-normal font-bold text-txtPrimary flex justify-center items-center gap-[5px]">
                <span className="uppercase">Total</span>{" "}
                <CustomTooltip
                  maxwidth="430px"
                  place="top"
                  id="total-payments-anchor"
                  content={`<div class="text-xs leading-[18px] font-normal">Total plan amount with <span class="lowercase">
                  ${
                    portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "convenience fee"
                  }
                </span> included.</div>`}
                >
                  <div
                    id="total-payments-anchor"
                    className="cursor-pointer pointer-events-auto"
                  >
                    <InfoIcon />
                  </div>
                </CustomTooltip>
              </div>
              <div className="flex items-center gap-2 text-txtPrimary">
                <Skeleton width={100} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* schedule */}
      {orderData?.recurring_type === "schedule" &&
        scheduledDetails?.plan_id === 0 && (
          <div className="lg:px-10 px-5 pb-2">
            <div className=" py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg mt-2">
              <div className="flex flex-col items-start gap-1">
                <div className="px-6 text-sm leading-normal font-bold uppercase">
                  {isOffer ? "OFFER TERMS" : "TERMS"}
                </div>
                {isOffer ? (
                  ""
                ) : (
                  <div className="px-6 w-full">
                    <div className=" mt-2 h-[1px] w-full bg-strokePrimary" />
                  </div>
                )}
                <div className="flex flex-col items-start w-full">
                  {isOffer ? (
                    <>
                      <div className="flex bg-bgThird w-full gap-4 px-6 py-5 mt-[12px]">
                        <div>
                          <OfferIcon />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-txtPrimary">
                            {scheduledDetails?.offerDetails?.headline}
                          </p>
                          <p className="text-sm font-normal text-txtParagraph">
                            {scheduledDetails?.offerDetails?.description}
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                </div>
                <div className=" px-6 pt-4 flex flex-col items-start gap-4 w-full">
                  <div className="flex items-center justify-between w-full">
                    <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                      Start date
                      <CustomTooltip
                        maxwidth="430px"
                        place="top"
                        id="start-date-anchor"
                        content={`<div class="text-xs leading-[18px] font-normal  max-w-[250px]">The date when the first payment installment is scheduled to be processed.</div>`}
                      >
                        <InfoIcon
                          id="start-date-anchor"
                          className="cursor-pointer group"
                          _className="group-hover:!stroke-iconObscure"
                        />
                      </CustomTooltip>
                    </div>
                    <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                      {isLoading ? (
                        <Skeleton width={80} />
                      ) : (
                        scheduled &&
                        scheduled.length > 0 &&
                        formatPaymentListDate(
                          scheduled[0].billing_date,
                          userData?.data?.accountData?.timezone,
                          "short"
                        )
                      )}
                    </div>
                  </div>
                  <div className="h-[1px] w-full bg-strokeSecondary" />

                  <div className="flex items-center justify-between w-full">
                    <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                      Frequency{" "}
                      <CustomTooltip
                        maxwidth="430px"
                        place="top"
                        id="frequency-anchor"
                        content={`<div class="text-xs leading-[18px] font-normal max-w-[250px]">Defines how often the installments will be processed.</div>`}
                      >
                        <div
                          id="frequency-anchor"
                          className="cursor-pointer pointer-events-auto"
                        >
                          <InfoIcon />
                        </div>
                      </CustomTooltip>
                    </div>
                    <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                      {isLoading ? (
                        <Skeleton width={100} />
                      ) : (
                        <>
                          {changePaymentScheduleName(
                            scheduledDetails?.payment_schedule
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="h-[1px] w-full bg-strokeSecondary" />

                  <div className="flex items-center justify-between w-full">
                    <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                      Number of transactions{" "}
                      <CustomTooltip
                        maxwidth="280px"
                        place="top"
                        id="transaction-anchor"
                        content={`<div class="text-xs leading-[18px] font-normal">The total number of payment installments within this schedule.</div>`}
                      >
                        <div
                          id="transaction-anchor"
                          className="cursor-pointer pointer-events-auto"
                        >
                          <InfoIcon />
                        </div>
                      </CustomTooltip>
                    </div>
                    <div className="font-medium text-sm leading-normal text-txtPrimary text-right capitalize">
                      {isLoading ? (
                        <Skeleton width={50} />
                      ) : (
                        scheduledDetails?.total_payments
                      )}
                    </div>
                  </div>

                  {isOffer && (
                    <>
                      <div className="h-[1px] w-full bg-strokePrimary" />
                      <div className="flex items-center justify-between w-full">
                        <div className="flex font-bold items-center gap-1 text-sm leading-normal text-txtPrimary">
                          <p>Discount amount</p>
                          <div className="grid place-items-center">
                            <CustomTooltip
                              maxwidth="430px"
                              place="top"
                              id="total-payments-anchor"
                              content={`<div class="text-xs leading-[18px] font-normal">Total discounted amount for this plan.</div>`}
                            >
                              <div
                                id="total-payments-anchor"
                                className="cursor-pointer pointer-events-auto"
                              >
                                <InfoIcon />
                              </div>
                            </CustomTooltip>
                          </div>
                        </div>
                        <div className="font-bold text-sm text-txtPrimary">
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            formatCurrency(
                              scheduledDetails?.offerDetails?.settlement_amount
                            ) || "$0.00"
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="lg:px-6 px-4  py-5 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg">
              <div className="flex items-center justify-between w-full">
                <div className="text-xl leading-normal font-bold text-txtPrimary flex justify-center items-center gap-[5px]">
                  <span className="uppercase">Total</span>{" "}
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="total-payments-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal">Total plan amount with <span class="lowercase">
                  ${
                    portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "convenience fee"
                  }
                </span> included.</div>`}
                  >
                    <div
                      id="total-payments-anchor"
                      className="cursor-pointer pointer-events-auto"
                    >
                      <InfoIcon />
                    </div>
                  </CustomTooltip>
                </div>
                <div className="flex items-center gap-2 text-txtPrimary font-bold">
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    formatCurrency(orderData?.grandTotal) || "$0.00"
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* productSubscriptions */}
      {orderData?.recurring_type === "subscription" && (
        <div className="lg:px-10 px-5 py-2">
          <div className="lg:px-6 px-4  py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
            <div className="flex flex-col items-start gap-5">
              <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                Current Plan
              </div>
              <div className="h-[1px] w-full bg-strokePrimary" />

              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                  Start date
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="start-date-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal max-w-[250px]">The date when the first payment installment is scheduled to be processed.</div>`}
                  >
                    <InfoIcon
                      id="start-date-anchor"
                      className="cursor-pointer group"
                      _className="group-hover:!stroke-iconObscure"
                    />
                  </CustomTooltip>
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={80} />
                  ) : (
                    formatPaymentListDate(
                      orderData?.start_sub || orderData?.insert_date,
                      userData?.data?.accountData?.timezone,
                      "short"
                    )
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                  Frequency{" "}
                  <CustomTooltip
                    maxwidth="430px"
                    place="top"
                    id="frequency-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal max-w-[250px]">Defines how often the installments will be processed.</div>`}
                  >
                    <div
                      id="frequency-anchor"
                      className="cursor-pointer pointer-events-auto"
                    >
                      <InfoIcon />
                    </div>
                  </CustomTooltip>
                </div>
                <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    <>
                      {changePaymentScheduleName(
                        scheduledDetails?.payment_schedule,
                        products,
                        orderData?.recurring_type
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between w-full">
                <div className="font-normal text-sm leading-normal text-txtSecondary flex justify-center items-center gap-[4px]">
                  {dateStatusAndDesc?.status}
                  <CustomTooltip
                    maxwidth="280px"
                    place="top"
                    id="next-payment-anchor"
                    content={`<div class="text-xs leading-[18px] font-normal">${dateStatusAndDesc?.desc}</div>`}
                  >
                    <div
                      id="next-payment-anchor"
                      className="cursor-pointer pointer-events-auto"
                    >
                      <InfoIcon />
                    </div>
                  </CustomTooltip>
                </div>

                <div className="font-medium text-sm leading-normal text-txtPrimary text-right capitalize">
                  {isLoading ? (
                    <Skeleton width={50} />
                  ) : (
                    formatPaymentListDate(
                      payment_date,
                      userData?.data?.accountData?.timezone,
                      "short"
                    )
                  )}
                </div>
              </div>
              <div className="h-[1px] w-full bg-strokePrimary" />
              <div className="flex w-full flex-col gap-4">
                {products.length > 0 &&
                  products
                    .filter(
                      (x: any) => x?.priceDetails?.frequency === "recurring"
                    )
                    .map((item: any, index: number) => (
                      <div
                        className="flex items-center justify-between w-full"
                        key={index}
                      >
                        <div className="font-medium text-sm leading-normal text-txtPrimary flex justify-center items-center gap-[4px]">
                          {item?.productDetails?.name}
                          {item?.quantity > 1 ? ` (x${item?.quantity})` : ""}
                        </div>
                        <div className="font-medium text-sm leading-normal text-txtPrimary">
                          {formatCurrency(item?.amount * item?.quantity) ||
                            "$0.00"}{" "}
                          {item?.priceDetails?.schedule &&
                            item?.priceDetails?.schedule !== "" &&
                            `/ ${changeScheduleName(item?.priceDetails)}`}
                        </div>
                      </div>
                    ))}
              </div>
            </div>
          </div>
          <div className="lg:px-6 px-4  py-5 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg">
            <div className="flex items-center justify-between w-full">
              <div className="text-xl leading-normal font-bold text-txtPrimary flex justify-center items-center gap-[5px]">
                <span className="uppercase">Total</span>{" "}
                <CustomTooltip
                  maxwidth="430px"
                  place="top"
                  id="total-payments-anchor"
                  content={`<div class="text-xs leading-[18px] font-normal">A <span class="lowercase">
                  ${
                    portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "convenience fee"
                  }
                </span> may apply to each payment.</div>`}
                >
                  <div
                    id="total-payments-anchor"
                    className="cursor-pointer pointer-events-auto"
                  >
                    <InfoIcon />
                  </div>
                </CustomTooltip>
              </div>
              <div className="flex items-center gap-2 text-txtPrimary font-bold">
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  formatCurrency(
                    orderData?.productAmount
                    // (orderData?.cc_fee_amount > 0
                    //   ? orderData?.cc_fee_amount
                    //   : orderData?.ach_fee_amount),
                  ) || "$0.00"
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* invoice list */}
      {financialSettings?.show_invoice_history === SettingsValue.YES &&
      invoiceList?.length &&
      !isLoading ? (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="lg:px-6 px-4  py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full">
            <div className="flex flex-col items-start gap-5">
              <div className="flex items-center justify-between w-full">
                <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                  Invoice History
                </div>
              </div>
              <div className="w-full h-[1px] bg-strokePrimary" />
              {invoiceList?.map((x: any, index: number) => (
                <div className="flex items-center w-full" key={index}>
                  <div
                    className={`w-1/3 text-sm text-txtParagraph leading-normal text-start flex gap-[6px] justify-start items-center`}
                  >
                    {x?.creation_date
                      ? moment(x?.creation_date).format("MM/DD/YYYY")
                      : ""}
                    <span
                      className="cursor-pointer"
                      onClick={() => {
                        setIsInvoicePreview(true);
                        setIsInvoicePreviewId(x.url_slug);
                      }}
                    >
                      {(x?.status?.toLowerCase() === "paid" ||
                        x?.status?.toLowerCase() === "open") && (
                        <ExportIcon
                          className="group"
                          _className="group-hover:!fill-iconObscure"
                        />
                      )}
                    </span>
                  </div>
                  <div
                    className={`w-1/3 text-sm text-txtPrimary leading-normal flex justify-center`}
                  >
                    <div
                      className={`px-2 rounded-md py-[3px] w-max capitalize ${getStatusName(x?.status).className}`}
                    >
                      {getStatusName(x?.status).label}
                    </div>
                  </div>
                  {/* {(x?.cc_fee_amount > 0 || x?.ach_fee_amount > 0) && (
                    <div
                      className={`w-1/3 text-sm text-txtPrimary leading-normal text-center`}
                    >
                      {x?.cc_fee_amount > 0
                        ? formatCurrency(x?.cc_fee_amount)
                        : formatCurrency(x?.ach_fee_amount)}
                    </div>
                  )} */}
                  <div
                    className={`w-1/3 text-sm text-txtPrimary leading-normal text-end`}
                  >
                    {x?.total_amount
                      ? formatCurrency(x?.total_amount)
                      : "$0.00"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {scheduledDetails?.plan_id > 0 && !isLoading && agreementData && (
        <div className="lg:px-10 px-5 pb-2">
          <div className=" py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg mt-2">
            <div className="flex flex-col items-start gap-1">
              <div className="px-6 text-sm leading-normal font-bold uppercase">
                {isOffer ? "OFFER TERMS" : "TERMS"}
              </div>
              {isOffer ? (
                ""
              ) : (
                <div className="px-6 w-full">
                  <div className=" mt-2 h-[1px] w-full bg-strokePrimary" />
                </div>
              )}

              <div className="flex flex-col items-start w-full">
                {isOffer ? (
                  <>
                    <div className="flex bg-bgThird w-full gap-4 px-6 py-5 mt-[12px]">
                      <div>
                        <OfferIcon />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-txtPrimary">
                          {agreementData?.offer_headline}
                        </p>
                        <p className="text-sm font-normal text-txtParagraph">
                          {agreementData?.offer_description}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  ""
                )}
              </div>
              <div className="px-6 pt-4 flex flex-col items-start gap-5 w-full ">
                <div className=" flex items-center justify-between w-full">
                  <div className="font-normal flex items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                    <p>Start date </p>
                    <CustomTooltip
                      maxwidth="430px"
                      place="top"
                      id="start-date-anchor"
                      content={`<div class="text-xs leading-[18px] font-normal">The date when the first payment installment is scheduled to be processed.</div>`}
                    >
                      <InfoIcon
                        id="start-date-anchor"
                        className="cursor-pointer group"
                        _className="group-hover:!stroke-iconObscure"
                      />
                    </CustomTooltip>
                  </div>
                  <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                    {isLoading ? (
                      <Skeleton width={150} />
                    ) : (
                      formatPaymentListDate(
                        agreementData?.billing_start_date,
                        agreementData?.agreement_tz,
                        "long"
                      )
                    )}
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary" />
                <div className="flex items-center justify-between w-full">
                  <div className="font-normal flex items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                    <p>Frequency</p>{" "}
                    <div className="grid place-items-center">
                      <CustomTooltip
                        maxwidth="430px"
                        place="top"
                        id="frequency-anchor"
                        content={`<div class="text-xs leading-[18px] font-normal">Defines how often the installments will be processed.</div>`}
                      >
                        <div
                          id="frequency-anchor"
                          className="cursor-pointer pointer-events-auto"
                        >
                          <InfoIcon />
                        </div>
                      </CustomTooltip>
                    </div>
                  </div>
                  <div className="font-normal text-sm leading-normal text-txtPrimary">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      <>
                        {changePaymentScheduleName(
                          agreementData?.payment_schedule
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary" />

                <div className="flex items-center justify-between w-full">
                  <div className="flex font-normal items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                    <p>Number of transactions</p>
                    <div className="grid place-items-center">
                      <CustomTooltip
                        maxwidth="280px"
                        place="top"
                        id="transaction-anchor"
                        content={`<div class="text-xs leading-[18px] font-normal">The total number of payment installments within this schedule.</div>`}
                      >
                        <div
                          id="transaction-anchor"
                          className="cursor-pointer pointer-events-auto"
                        >
                          <InfoIcon />
                        </div>
                      </CustomTooltip>
                    </div>
                  </div>
                  <div className="font-normal text-sm leading-normal text-txtPrimary">
                    {isLoading ? (
                      <Skeleton width={80} />
                    ) : (
                      agreementData?.total_payments
                    )}
                  </div>
                </div>

                {isOffer && (
                  <>
                    <div className="h-[1px] w-full bg-strokePrimary" />
                    <div className="flex items-center justify-between w-full">
                      <div className="flex font-bold items-center gap-1 text-sm leading-normal text-txtPrimary">
                        <p>Discount amount</p>
                        <div className="grid place-items-center">
                          <CustomTooltip
                            maxwidth="430px"
                            place="top"
                            id="total-payments-anchor"
                            content={`<div class="text-xs leading-[18px] font-normal">Total discounted amount for this plan.</div>`}
                          >
                            <div
                              id="total-payments-anchor"
                              className="cursor-pointer pointer-events-auto"
                            >
                              <InfoIcon />
                            </div>
                          </CustomTooltip>
                        </div>
                      </div>
                      <div className="font-bold text-sm text-txtPrimary">
                        {isLoading ? (
                          <Skeleton width={80} />
                        ) : (
                          formatCurrency(
                            agreementData?.offer_type === "P" &&
                              (agreementData?.offer_percentage || 0) > 0
                              ? (agreementData?.offer_percentage *
                                  agreementData?.initial_balance) /
                                  100
                              : agreementData?.offer_amount
                          ) || "$0.00"
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="px-6 py-5 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg border-t ">
            <div className="flex items-center justify-between w-full">
              <div className="text-xl leading-normal font-bold flex justify-center items-center text-txtPrimary gap-1">
                <span className="uppercase">Total</span>{" "}
                <CustomTooltip
                  maxwidth="430px"
                  place="top"
                  id="total-payments-anchor"
                  content={`<div class="text-xs leading-[18px] font-normal">Total of all scheduled payments across the payment plan’s duration.</div>`}
                >
                  <div
                    id="total-payments-anchor"
                    className="cursor-pointer pointer-events-auto"
                  >
                    <InfoIcon />
                  </div>
                </CustomTooltip>
              </div>

              <div className="text-xl flex items-center gap-2 text-txtPrimary font-bold">
                {isLoading ? (
                  <Skeleton width={100} />
                ) : isOffer ? (
                  formatCurrency(orderData?.pay_amount) || "$0.00"
                ) : (
                  formatCurrency(orderData?.pay_amount) || "$0.00"
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* schedule details */}
      {scheduled?.length && !isLoading ? (
        <div className="lg:px-10 px-5 py-2 animate__animated animate__fadeIn">
          <div className="flex flex-col items-start gap-4 w-full">
            <div className="lg:px-6 px-4  py-5 bg-bgPrimary border border-strokePrimary rounded-lg w-full overflow-x-scroll">
              <div className="flex flex-col items-start gap-5">
                <div className="flex items-center justify-between gap-3 w-full lg:flex-nowrap flex-wrap">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary">
                    SCHEDULE
                  </div>
                  <div className="flex items-center gap-2.5 text-txtSecondary text-xs justify-end">
                    <p>
                      Frequency:{" "}
                      <span className="text-txtPrimary font-medium">
                        {changePaymentScheduleName(
                          scheduledDetails?.payment_schedule
                        )}
                      </span>
                    </p>
                    <p>|</p>
                    <p>
                      Remaining payments:{" "}
                      <span className="text-txtPrimary capitalize font-medium">
                        {scheduledDetails?.remaining_payments || "--"}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[384px]" />
                <div className="flex items-center w-full">
                  <div
                    className={`${
                      orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-start`}
                  >
                    Payment Date
                  </div>
                  <div
                    className={`${
                      orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-center`}
                  >
                    Status
                  </div>
                  {orderData?.c_FeeAmount ? (
                    <div
                      className={`${
                        orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                      } min-w-[56px] text-sm text-txtParagraph leading-normal text-center`}
                    >
                      {orderData?.isStax ? "Surcharge" : "Fee"}
                    </div>
                  ) : null}
                  <div
                    className={`${
                      orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                    } min-w-[96px] text-sm text-txtParagraph leading-normal text-end`}
                  >
                    Amount
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[384px]" />
                {scheduled?.length
                  ? scheduled?.map((x: any, index: number) => {
                      return (
                        <div className="flex items-center w-full" key={index}>
                          <div
                            className={`${
                              orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                            } min-w-[96px] text-sm text-txtParagraph leading-normal text-start flex gap-[6px] justify-start items-center`}
                          >
                            {x?.billing_date
                              ? moment(x?.billing_date).format("MM/DD/YYYY")
                              : x?.payment_date
                                ? moment(x?.payment_date).format("MM/DD/YYYY")
                                : "--"}
                            <span
                              className="cursor-pointer"
                              onClick={() => {
                                setIsPaymentPreview(true);
                                setIsPaymentPreviewId(x.id);
                              }}
                            >
                              {(x?.status === "Succeeded" ||
                                x?.status === "disputeWon" ||
                                x.status === "Disputed") && (
                                <ExportIcon
                                  className="group"
                                  _className="group-hover:!fill-iconObscure"
                                />
                              )}
                            </span>
                          </div>
                          <div
                            className={`${
                              orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                            } min-w-[96px] text-sm text-txtPrimary leading-normal flex justify-center`}
                          >
                            <div
                              className={`px-2 rounded-md py-[3px] w-max ${
                                x.status === "Pending"
                                  ? "bg-[#FDB6000C] text-yellow"
                                  : x.status === "Approved"
                                    ? "bg-[#1678F20D] text-btnPrimaryActive"
                                    : x.status === "Declined" ||
                                        x.status === "Cancelled" ||
                                        x.status === "Failed" ||
                                        x.status?.toLowerCase() ===
                                          "disputed lost"
                                      ? "bg-[#FF00000D] text-red"
                                      : x.status === "Voided"
                                        ? "bg-[#FDB6000D] text-yellow"
                                        : x.status === "Succeeded" ||
                                            x.status?.toLowerCase() ===
                                              "disputewon"
                                          ? "bg-[#23B3390D] text-green"
                                          : x.status === "RefundedPartial" ||
                                              x.status === "Refunded"
                                            ? "bg-[#1678F20D] text-blue"
                                            : x.status === "Disputed"
                                              ? "bg-[#4F576A0D] text-iconBright"
                                              : ""
                              }`}
                            >
                              {(x.status && x.status === "Declined") ||
                              x?.status === "disputed lost"
                                ? "Failed"
                                : x.status === "Cancelled"
                                  ? "Canceled"
                                  : x?.status === "RefundedPartial"
                                    ? "Partial refund"
                                    : x?.status === "Succeeded" ||
                                        x?.status === "disputeWon"
                                      ? "Succeeded"
                                      : x?.status}
                            </div>
                            {x?.status === "Failed" ||
                              (x.status === "Declined" && (
                                <>
                                  <Button
                                    text="Pay"
                                    className="!text-xs !py-[2px] !px-2 ml-2"
                                    onClick={() => {
                                      if (!x?.order_id) return;
                                      let encodeString = encode(
                                        `${x?.order_id}_${orderData?.clusterId}_${new Date().getTime()}`,
                                        "pid_"
                                      );

                                      setFailedPayment(true);
                                      setFailedPaymentId(encodeString);
                                    }}
                                  />
                                </>
                              ))}
                          </div>
                          {orderData?.c_FeeAmount ? (
                            <div
                              className={`${
                                orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                              } min-w-[56px] text-sm text-txtPrimary leading-normal text-center`}
                            >
                              {x?.cf_amount
                                ? formatCurrency(x?.cf_amount)
                                : "--"}
                            </div>
                          ) : null}
                          <div
                            className={`${
                              orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                            } min-w-[96px] text-sm text-txtPrimary leading-normal text-end`}
                          >
                            {x?.amount ? formatCurrency(x?.amount) : "$0.00"}
                          </div>
                        </div>
                      );
                    })
                  : null}
                <div className="h-[1px] w-full bg-strokePrimary min-w-[384px]" />
                <div className="flex items-center w-full">
                  <div
                    className={`${
                      orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                    } text-xs leading-normal text-txtPrimary  min-w-[96px] `}
                  >
                    SUBTOTAL
                  </div>
                  <div
                    className={`${orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"} min-w-[96px] `}
                  ></div>
                  {orderData?.c_FeeAmount ? (
                    <div
                      className={`${
                        orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                      } text-center text-txtPrimary font-bold leading-normal min-w-[56px] `}
                    >
                      {formatCurrency(orderData?.c_FeeAmount) || "$0.00"}
                    </div>
                  ) : null}
                  <div
                    className={`${
                      orderData?.c_FeeAmount ? "w-1/4" : "w-1/3"
                    } text-end text-txtPrimary font-bold leading-normal min-w-[96px] `}
                  >
                    {formatCurrency(orderData?.pay_amount) || "$0.00"}
                  </div>
                </div>
                <div className="h-[1px] w-full bg-strokePrimary min-w-[384px]" />
                <div className="flex items-center justify-between w-full">
                  <div className="text-sm leading-normal font-bold uppercase text-txtPrimary  min-w-[152px]">
                    Total
                  </div>
                  <div className="text-lg leading-normal font-bold text-txtPrimary min-w-[192px] text-end">
                    {formatCurrency(orderData?.grandTotal) || "$0.00"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* footer */}
      {!isLoading && (
        <div
          className={`py-5 lg:px-10 px-5 bottom-0 ${
            scheduled?.length > 0 ||
            orderData?.recurring_type === "subscription"
              ? "static"
              : "fixed"
          }`}
        >
          <QuestionsFooter isPreview={true} />
        </div>
      )}

      <div
        className="absolute top-0 lg:w-[590px] h-full lg:right-[590px] right-0 z-10 w-full"
        style={{ display: isPaymentPreview ? "block" : "none" }}
      >
        <PaymentPreview
          id={isPaymentPreviewId}
          setId={setIsPaymentPreviewId}
          show={isPaymentPreview}
          setShow={setIsPaymentPreview}
          onClose={() => {
            setIsPaymentPreview(false);
            setIsPaymentPreviewId("");
            setShow(true);
          }}
          onDownloadCLick={(id: any, receiptId?: any) =>
            onPaymentDownload(id, receiptId)
          }
          isDownloading={isDownloading?.toString()}
          accountId={orderData?.accountId}
        />
      </div>
      <div
        className="absolute top-0 lg:w-[590px] h-full lg:right-[590px] right-0 z-10 w-full"
        style={{ display: isInvoicePreview ? "block" : "none" }}
      >
        <InvoicePreview
          id={isInvoicePreviewId}
          slug={isInvoicePreviewId}
          setId={setIsInvoicePreviewId}
          setIsPreview={setIsInvoicePreview}
          isPreview={isInvoicePreview}
          isSlug={false}
        />
      </div>

      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={failedPayment}
        size={`${isLessThan680 ? `100vw` : "680px"}`}
        onClose={() => {
          setFailedPayment(false);
          setFailedPaymentId(null);
        }}
        overlayColor="#000"
      >
        <FailedPayment
          id={failedPaymentId}
          show={failedPayment}
          setShow={setFailedPayment}
          setId={setFailedPaymentId}
          fetchDetails={() => onGetPaymentReceipt()}
        />
      </Drawer>
    </div>
  );
};

export default SubscriptionsPreview;
